import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import DesktopLayout from 'shared/components/layout/desktop-layout';
import MobileRoomLayout from 'shared/components/layout/mobile-layout';
import { ConnectWallet } from 'shared/components/sections/connect-wallet/connect-wallet';

import { usePrizeBoxPage } from './hooks/use-prize-box-page';
import { PrizeBoxMainContent } from './prize-box-main-content';
import styles from './prize-box-page.module.css';
import { GamePhase } from './prize-box-page.types';
import { FooterControls } from './prize-box-page.utils';

export const PrizeBoxPage: FC = () => {
  const { t } = useTranslation();
  const {
    isMobile,
    isConnected,
    address,
    loading,
    error,
    phase,
    selectedMove,
    aiMove,
    selectedMoveTopHand,
    result,
    pool,
    points,
    quotaResetAt,
    extraCredits,
    snapshotPoints,
    playerWins,
    aiWins,
    lastMoves,
    lastRoundOutcome,
    recoveryAmount,
    attemptsLeft,
    handleSelect,
    closeResult,
    buyAttempts,
    canPlayNow,
    loadingApprove,
    currentSlotBalance,
  } = usePrizeBoxPage();

  const footer = (
    <FooterControls
      phase={phase}
      attemptsLeft={attemptsLeft}
      onSelect={handleSelect}
      onBuy={buyAttempts}
      onTryAgain={closeResult}
      selectedMove={selectedMove}
      selecting={phase === GamePhase.Selecting}
      canPlayNow={canPlayNow}
      loadingApprove={loadingApprove}
      poolBalance={pool}
    />
  );

  if (!isConnected) {
    return <ConnectWallet title={'common.connectWallet'} description={'prizeBox.connectDesc'} />;
  }

  if (isMobile) {
    return (
      <MobileRoomLayout
        title={t('pages.prize')}
        className="flex flex-col sm:pb-[unset]"
        footerClassNames={clsx(styles.footer, 'pb-3 pt-3 sm:h-[unset]')}
        footer={!loading ? footer : null}
      >
        <div className="flex flex-1 grow flex-col">
          <PrizeBoxMainContent
            loading={loading}
            pool={pool}
            phase={phase}
            attemptsLeft={attemptsLeft}
            points={Number(points)}
            snapshotPoints={Number(snapshotPoints)}
            playerWins={playerWins}
            aiWins={aiWins}
            lastMoves={lastMoves}
            selectedMove={selectedMove}
            aiMove={aiMove}
            lastRoundOutcome={lastRoundOutcome}
            quotaResetAt={quotaResetAt}
            extraCredits={extraCredits}
            recoveryAmount={recoveryAmount}
            result={result}
            closeResult={closeResult}
            selectedMoveTopHand={selectedMoveTopHand}
            error={error}
            currentSlotBalance={currentSlotBalance}
          />
        </div>
      </MobileRoomLayout>
    );
  }

  return (
    <DesktopLayout title={t('pages.prize')} isGuestMode={!address} className="h-full">
      <div className="relative mx-auto flex h-full w-full max-w-screen-xs flex-col sm:max-w-screen-sm">
        <div className="flex flex-1 grow flex-col py-10">
          <PrizeBoxMainContent
            loading={loading}
            pool={pool}
            phase={phase}
            attemptsLeft={attemptsLeft}
            points={Number(points)}
            snapshotPoints={Number(snapshotPoints)}
            playerWins={playerWins}
            aiWins={aiWins}
            lastMoves={lastMoves}
            selectedMove={selectedMove}
            aiMove={aiMove}
            lastRoundOutcome={lastRoundOutcome}
            quotaResetAt={quotaResetAt}
            extraCredits={extraCredits}
            recoveryAmount={recoveryAmount}
            result={result}
            closeResult={closeResult}
            selectedMoveTopHand={selectedMoveTopHand}
            error={error}
            currentSlotBalance={currentSlotBalance}
          />
        </div>
        {!loading ? <div className="mt-12">{footer}</div> : null}
      </div>
    </DesktopLayout>
  );
};
